import { useEffect, useRef, useState } from "react";

import { Button, LoadingButton } from "ui/button";
import { TextField } from "ui/text-field";
import { Stack } from "ui/stack";

import { Checkbox } from "ui/checkbox";
import { Form } from "ui/forms";
import { TextArea } from "ui/text-area";

import { Comment, CommentReply, NewComment } from "types/comments";

interface CommentInputProps {
  openOnDefault?: boolean;
  commentType?: "full" | "text-only";
  parentId?: string;
  submitNewComment: (comment: NewComment) => Promise<Comment | CommentReply | void>;
  isClientUser: boolean;
  isDisabled?: boolean;
}

export const CommentInput = ({
  openOnDefault = false,
  commentType = "full",
  parentId,
  submitNewComment,
  isClientUser,
  isDisabled = false,
}: CommentInputProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(openOnDefault);
  const [comment, setComment] = useState<string>("");
  const [isActionItem, setIsActionItem] = useState<boolean>(true);
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    commentType === "text-only" ? handleTextOnlySubmit() : handleNewCommentSubmit();
  };

  const handleNewCommentSubmit = () => {
    setIsLoading(true);

    const payload: NewComment = {
      actionItem: isActionItem,
      hidden: !isHidden,
      text: comment,
    };

    if (comment.length <= 0) {
      setIsLoading(false);
    }

    submitNewComment(payload);
    setInitialState();
  };

  const handleTextOnlySubmit = () => {
    setIsLoading(true);
    const payload: NewComment = {
      text: comment,
      actionItem: isActionItem,
      parentId: parentId,
    };

    submitNewComment(payload).finally(() => {
      setInitialState();
      setIsLoading(false);
    });
  };

  const setInitialState = () => {
    setIsExpanded(false);
    setComment("");
    setIsActionItem(true);
    setIsHidden(false);
    setIsLoading(false);
  };

  useEffect(() => {
    isExpanded && inputRef.current?.focus();
  }, [isExpanded]);

  return !isExpanded ? (
    <TextField
      placeholder={commentType === "text-only" ? "Add a reply" : "Add a comment"}
      autoComplete="off"
      onFocus={() => setIsExpanded((isExpanded) => !isExpanded)}
      data-testid="comment-input-collapsed"
      disabled={isDisabled}
    />
  ) : (
    <Form spacing={2} onSubmit={handleSubmit}>
      <TextArea
        ref={inputRef}
        rows={4}
        resize="none"
        disabled={isDisabled}
        placeholder={commentType === "text-only" ? "Add a reply" : "Add a comment"}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        data-testid={commentType === "text-only" ? "comment-input-reply" : "comment-input-main"}
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            handleSubmit(e);
          }
        }}
      />
      {/* Stack spacing is 8px, not 4px: the checkbox no longer carries MUI's 9px padding. */}
      {commentType === "full" && !isClientUser && (
        <Stack spacing={1}>
          <Checkbox
            checked={isHidden}
            disabled={isDisabled}
            onChange={setIsHidden}
            size="sm"
            label="Visible to client"
            data-testid="comment-input-checkbox-hidden"
          />
          <Checkbox
            checked={isActionItem}
            disabled={isDisabled}
            onChange={setIsActionItem}
            size="sm"
            label="Flag as action item"
            data-testid="comment-input-checkbox-actionitem"
          />
        </Stack>
      )}
      <div className="flex gap-4 pb-4 justify-end">
        <Button data-testid="comment-input-cancel" variant="outlined" onClick={setInitialState}>
          Cancel
        </Button>
        <LoadingButton
          data-testid="comment-input-submit"
          type="submit"
          state={isLoading ? "loading" : "default"}
          loadingMessage="Submitting..."
          successMessage="Submitted"
          errorMessage="Error. Try again."
          disabled={comment === "" || isDisabled}>
          Submit
        </LoadingButton>
      </div>
    </Form>
  );
};
